import { AutocompleteSelect, Checkbox, Modal, ModalSizes, TextInput } from "@centricsoftware/design-system";
import { NODE_TYPE } from "c8-platform";
import { SingleSelectWithOptionStore } from "csi/components/index";
import DojoView from "csi/page/DojoView";
import { ATTR_AUTOFILL_DEPENDENT_ENUM, ATTR_DEPENDS_ON, ATTR_REFLECTION_ATTR_OWNER, ATTR_SUBTYPE, URL_SITE } from "csi/reflection/reflectionConstants";
import { PopupDialog } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import AttributeDefinitionSectionUOM from "./AttributeDefinitionSectionUOM";
import { useAttributeDefinitionStore } from "./AttributeDefinitionStore";

// TODO: Try to use disablePortal={false} on Modal - see comments below

const AttributeOwnerClass: FunctionComponent = observer(() => {
	const { 
		isBuiltInOrUpdated,
		isNewFromInherited,
		loc, 
		ownerClass,
		ownerOptions,
		onChangeOwnerClass,
	} = useAttributeDefinitionStore();
	const label = loc.attributeResource(NODE_TYPE.CUSTOM_ATTRIBUTE, ATTR_REFLECTION_ATTR_OWNER);

	if (ownerOptions) {
		return (
			<AutocompleteSelect
				dataCsiAutomation={ATTR_REFLECTION_ATTR_OWNER}
				disableClearable
				dropdownButtonAriaLabel={label}
				label={label}
				onChange={onChangeOwnerClass}
				options={ownerOptions}
				readOnly={isBuiltInOrUpdated || isNewFromInherited}
				value={ownerClass}
			/>
		);
	}
});

const AttributeDefinitionDependsOn: FunctionComponent = observer(() => {
	const { 
		attrAutofillDependent,
		dependsOnAttribute,
		dependsOnDataStore,
		loc, 
		locale,
		onChangeAutofillDependent,
		onChangeDependsOnAttribute,
	} = useAttributeDefinitionStore();

	if (dependsOnDataStore) {
		return (
			<>
				<SingleSelectWithOptionStore 
					dataCsiAutomation={ATTR_DEPENDS_ON}
					label={loc.attributeResource(NODE_TYPE.CUSTOM_ATTRIBUTE, ATTR_DEPENDS_ON)}
					onChange={onChangeDependsOnAttribute}
					optionsStore={dependsOnDataStore}
					value={dependsOnAttribute}
				/>
				{dependsOnAttribute && (
					<Checkbox
						checked={attrAutofillDependent}
						dataCsiAutomation={ATTR_AUTOFILL_DEPENDENT_ENUM}
						labelRes={locale.labelAutofillDependentEnum}
						onChange={onChangeAutofillDependent}
					/>
				)}
			</>
		);
	}
});

const AttributeDefinitionTargetClassSubtype: FunctionComponent = observer(() => {
	const { 
		isBuiltInOrUpdated,
		isRefToSubtypedClass,
		loc, 
		onChangeTargetClassSubtype,
		targetClassSubtypeDataStore,
		targetClassSubtype,
		targetClassSubtypeError,
	} = useAttributeDefinitionStore();

	if (isRefToSubtypedClass) {
		return (
			<SingleSelectWithOptionStore
				autofillIfOne
				dataCsiAutomation={ATTR_SUBTYPE}
				disableClearable
				error={!!targetClassSubtypeError}
				errorMessage={targetClassSubtypeError}
				label={loc.attributeResource(NODE_TYPE.CUSTOM_ATTRIBUTE, ATTR_SUBTYPE)}
				onChange={onChangeTargetClassSubtype}
				optionsStore={targetClassSubtypeDataStore}
				readOnly={isBuiltInOrUpdated}
				required={!isBuiltInOrUpdated}
				value={targetClassSubtype}
			/>
		);
	}
});

const AttributeDefinitionTargetClass: FunctionComponent = observer(() => {
	const { 
		isBuiltInOrUpdated,
		isRefType,
		loc, 
		onChangeTargetClass,
		targetClass,
		targetClassError,
		targetClassOptions,
	} = useAttributeDefinitionStore();

	if (isRefType) {
		const labelTargetClassName = loc.attributeResource("CustomAttribute", "TargetClassName");
		return (
			<AutocompleteSelect
				dataCsiAutomation="TargetClassName"
				disableClearable
				dropdownButtonAriaLabel={labelTargetClassName}
				error={!!targetClassError}
				errorMessage={targetClassError}
				label={labelTargetClassName}
				onChange={onChangeTargetClass}
				options={targetClassOptions}
				readOnly={isBuiltInOrUpdated}
				required={!isBuiltInOrUpdated}
				value={targetClass}
			/>
		);
	}
});

const AttributeDefinitionSectionMain: FunctionComponent = observer(() => {
	const store = useAttributeDefinitionStore();
	const {
		attrId,
		attrIdError,
		attrName,
		attrTooltip,
		attrType,
		attrSelectEnumerationError, 
		canSelectColumnWidth,
		columnWidth,
		columnWidthOptions, 
		editEnumerationViewParams,
		enumListDataStore,
		enumListValue,
		formatOptions,
		formatOptionsValue,
		isBuiltInOrUpdated,
		isEnumType,
		isFormatRequired,
		isNew,
		isTypeDisabled,
		locale, 
		onChangeAttrFormat,
		onChangeAttrId,
		onChangeAttrName, 
		onChangeAttrTooltip,
		onChangeAttrType, 
		onChangeColumnWidth, 
		onChangeSelectEnumeration, 
		onEditEnumerationsClickClose,
		onEditEnumerationsClickOpen,
		onEditEnumerationsViewCreated,
		openEnumerationsModal,
		typeOptions,
	} = store;

	const {
		helperTextAttributeIdRes,
		helperTextDisplayNameRes,
		labelCloseRes,
		labelConfigurableAttributeColumnWidth,
		labelEditEnumerationsRes,
		labelFormatRes,
		labelLocaleNameRes,
		labelReflectionAttributeIdRes,
		labelSelectEnumerationRes,
		labelTooltipRes,
		labelValueTypeRes,
	} = locale;

	return (
		<>
			<TextInput
				error={!!attrIdError}
				errorMessage={attrIdError}
				helperTextRes={isNew ? helperTextAttributeIdRes : undefined}
				labelRes={labelReflectionAttributeIdRes}
				onChange={onChangeAttrId}
				readOnly={!isNew}
				required={isNew}
				value={attrId}
			/>
			<TextInput
				helperTextRes={helperTextDisplayNameRes}
				labelRes={labelLocaleNameRes}
				onChange={onChangeAttrName}
				value={attrName}
			/>
			<AttributeOwnerClass />
			<AutocompleteSelect 
				disableClearable
				dropdownButtonAriaLabelRes={labelValueTypeRes}
				labelRes={labelValueTypeRes}
				onChange={onChangeAttrType}
				options={typeOptions}
				readOnly={isTypeDisabled}
				value={attrType}
			/>
			<AttributeDefinitionTargetClass /> 
			<AttributeDefinitionTargetClassSubtype /> 
			<AttributeDefinitionSectionUOM />
			{!!formatOptions.length && (
				<AutocompleteSelect
					disableClearable={isFormatRequired}
					dropdownButtonAriaLabelRes={labelFormatRes}
					labelRes={labelFormatRes}
					onChange={onChangeAttrFormat}
					options={formatOptions}
					required={isFormatRequired}
					value={formatOptionsValue}
				/>
			)}
			{isEnumType && (
				<SingleSelectWithOptionStore
					disableClearable
					error={!!attrSelectEnumerationError} 
					errorMessage={attrSelectEnumerationError}
					labelRes={labelSelectEnumerationRes}
					linkButtonProps={!isBuiltInOrUpdated && {
						childrenRes: labelEditEnumerationsRes,
						onClick: onEditEnumerationsClickOpen,
					}}
					onChange={onChangeSelectEnumeration}
					optionsStore={enumListDataStore}
					readOnly={isBuiltInOrUpdated}
					required={!isBuiltInOrUpdated}
					value={enumListValue}
				/>
			)}
			<AttributeDefinitionDependsOn /> 
			<TextInput
				labelRes={labelTooltipRes}
				onChange={onChangeAttrTooltip}
				value={attrTooltip}
			/>
			{canSelectColumnWidth && (
				<AutocompleteSelect
					dataCsiAutomation="ColumnWidth"
					disableClearable
					dropdownButtonAriaLabel={labelConfigurableAttributeColumnWidth}
					label={labelConfigurableAttributeColumnWidth}
					onChange={onChangeColumnWidth}
					options={columnWidthOptions}
					value={columnWidth}
				/>
			)}
			{/*
Current version of Modal has conflict with dojo poopup system - C8-76421
The conflict occur because by default modal is rendering modal dialog and it's content within 
html of parent component instead of using portal.
And dojo supposed that each popup "grows" from <body>
Together it is causing error in popup position calculation
Calculations happen in dojo module webpack:///node_modules/dijit/place.js line 289 function around()
New DS' modal supposed to have property disablePortals which could potentially fix the conflict by setting it to false
Unfortunately in new DS Modal has hardcoded disablePortal to true (line 44)
We need to try and test if issue will be fixed if disablePortal will be set to false - require DS changes.

			<Modal
				actionButtonProps={{
					childrenRes: labelCloseRes,
					onClick: onEditEnumerationsClickClose,
				}}
				dataCsiAutomation="AttributeDefinitionEnumModal"
				disableEnforceFocus
				onClose={onEditEnumerationsClickClose}
				open={openEnumerationsModal}
				showActionButton
				showFooter
				size={ModalSizes.MaxWidth}
				titleRes={labelEditEnumerationsRes}
			>
				<DojoView
					fullHeight
					nodeUrl={URL_SITE}
					onViewCreated={onEditEnumerationsViewCreated}
					style={{
						width: "1200px",
					}}
					viewId="Enumerations"
					viewParams={editEnumerationViewParams}
				/>
			</Modal>
*/}
			{/* To make this PopupDialog work with dojo popup, I have added property "disableEnforceFocus" to AttributeDefinitionPopup. It should be removed with PopupDialog removal*/}
			<PopupDialog
				disableEnforceFocus
				fullHeight
				onClose={onEditEnumerationsClickClose}
				open={openEnumerationsModal}
				titleRes={labelEditEnumerationsRes.res}
			>
				{{
					main: (
						<DojoView
							fullHeight
							nodeUrl={URL_SITE}
							onViewCreated={onEditEnumerationsViewCreated}
							style={{
								width: "1200px",
							}}
							viewId="Enumerations"
							viewParams={editEnumerationViewParams}
						/>
					),
				}}
			</PopupDialog>
		</>
	);
});

export default AttributeDefinitionSectionMain;